import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { RouteConstants } from "../../constants/route-constant";
import SmartFhirLoadingScreen from "../Shared/SmartFhirLoadingScreen";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { setProvider } from "../../store/providerReducer";
import { useFhirInitialization } from "../../hooks/useFhirInitialization";
import { useFhirClient } from "../../hooks/useFhirClient";
import { useHomeModuleAlertCounts } from "../../hooks/useHomeModuleAlertCounts";
import { STORAGE_KEYS } from "../../services/encrypt-decrypt";
import {
  prefetchCdiReferenceData,
  prefetchHomeReferenceData,
  prefetchClinicalCareGuidelines,
  prefetchEncoderCatalog,
  invalidateCareManagementQuery,
} from "../../hooks/queries";
import { DEFAULT_ENCODER_CATALOG } from "../../utils/encoder";
import { useProviderDetails } from "../../hooks/queries";
import {
  getHomeScreenProviderLabel,
} from "../../utils/provider-display-utils";
import SimpleCircularIndicator from "../Shared/indicator";
import useColors from "../../theme/useColors";

interface ModuleCardProps {
  title: string;
  description: string;
  logoSrc?: string;
  comingSoon?: boolean;
  alertCount?: number;
  onClick?: () => void;
  onPrefetch?: () => void;
}

/** One-column cards stay shorter; from two columns up, height tracks the viewport up to 200px. */
const CARD_HEIGHT_CLASS =
  "h-[6.5rem] min-h-0 min-[500px]:h-[clamp(8.75rem,calc(5rem+8vw),12.5rem)]";

const ACTIVE_CARD_CLASSES =
  `[container-type:inline-size] bg-white rounded-2xl border border-[#BDBDBD]/40 shadow relative overflow-hidden z-[1] w-full flex flex-col hover:shadow-lg transition-shadow cursor-pointer ${CARD_HEIGHT_CLASS}`;

const COMING_SOON_CARD_CLASSES =
  `[container-type:inline-size] bg-white rounded-2xl border border-[#BDBDBD]/40 shadow relative overflow-hidden z-[1] w-full flex flex-col cursor-default opacity-80 transition-shadow ${CARD_HEIGHT_CLASS}`;

/**
 * Below the four-column breakpoint, type and logos follow the viewport so 1-, 2-, and 3-column
 * layouts stay smaller. At `xl` they match the original card.
 */
const CARD_TITLE_TEXT_CLASS =
  "text-[clamp(0.875rem,calc(0.65rem+0.9vw),1.25rem)] md:text-[clamp(0.875rem,calc(0.55rem+1vw),1.125rem)] md:tracking-tight xl:text-2xl xl:tracking-normal";
const CARD_BODY_TEXT_CLASS =
  "text-[clamp(0.6875rem,calc(0.55rem+0.45vw),0.8125rem)] xl:text-sm";
const CARD_AI_LOGO_CLASS =
  "h-[clamp(0.875rem,calc(0.7rem+0.8vw),1.25rem)] w-auto xl:h-6";
const CARD_MODULE_LOGO_CLASS =
  "h-[clamp(0.5rem,calc(0.4rem+0.5vw),0.875rem)] w-auto xl:h-4";
const CARD_FAVICON_CLASS =
  "h-[clamp(0.5rem,calc(0.4rem+0.5vw),0.875rem)] w-auto xl:h-4";
const CARD_FAVICON_CLEARANCE_CLASS =
  "h-[clamp(0.5rem,calc(0.4rem+0.5vw),0.875rem)] w-[clamp(1.5rem,calc(1rem+1.5vw),2.25rem)] xl:h-4 xl:w-11";
const CARD_PADDING_CLASS = "p-[clamp(0.375rem,calc(0.25rem+0.6vw),0.625rem)] xl:p-3";
/** Floor only — leftover height from the shared card height sits in this gap via justify-between. */
const CARD_SECTION_GAP_CLASS = "gap-[clamp(0.25rem,calc(0.15rem+0.7vw),0.625rem)] xl:gap-3";
const CARD_LOGO_TO_DESC_GAP_CLASS =
  "gap-[clamp(0.125rem,calc(0.1rem+0.4vw),0.375rem)] xl:gap-2";
const CARD_ALERT_BADGE_CLASS =
  "top-[clamp(-0.4rem,calc(-0.15rem-0.35vw),-0.25rem)] right-[clamp(-0.4rem,calc(-0.15rem-0.35vw),-0.25rem)] h-[clamp(1.25rem,calc(0.85rem+0.9vw),1.5rem)] min-w-[clamp(1.25rem,calc(0.85rem+0.9vw),1.5rem)] px-[0.35em] text-[clamp(0.6875rem,calc(0.5rem+0.45vw),0.8125rem)] xl:top-[-0.4375rem] xl:right-[-0.4375rem] xl:h-7 xl:min-w-7 xl:text-[0.9375rem]";

const ModuleCard: React.FC<ModuleCardProps> = ({
  title,
  description,
  logoSrc,
  comingSoon = false,
  alertCount = 0,
  onClick,
  onPrefetch,
}) => {
  const colors = useColors();
  const outerClasses = comingSoon ? COMING_SOON_CARD_CLASSES : ACTIVE_CARD_CLASSES;

  const card = (
    <div
      className={outerClasses}
      onClick={comingSoon ? undefined : onClick}
      onMouseEnter={comingSoon ? undefined : onPrefetch}
      onFocus={comingSoon ? undefined : onPrefetch}
      role={comingSoon ? undefined : "button"}
      tabIndex={comingSoon ? undefined : 0}
      onKeyDown={
        comingSoon
          ? undefined
          : (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onClick?.();
              }
            }
      }
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -top-[150px] -right-[300px] -z-10 blur-3xl h-[300px] w-[400px] rounded-full bg-gradient-to-t from-[#FDECD4] to-[#FAF8F1]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-[150px] -left-[300px] -z-10 blur-3xl h-[300px] w-[400px] rounded-full bg-gradient-to-t from-[#FAF8F1] to-[#E7D0F3]"
      />
      <img
        src="/favicon.svg"
        alt=""
        aria-hidden
        className={`pointer-events-none absolute bottom-[clamp(0.5rem,2.5cqw,1.5rem)] right-[clamp(0.5rem,2.5cqw,1.5rem)] z-[1] shrink-0 object-contain opacity-70 ${CARD_FAVICON_CLASS}`}
      />
      {comingSoon && (
        <span className="absolute top-[clamp(0.5rem,2.5cqw,0.75rem)] right-[clamp(0.5rem,2.5cqw,0.75rem)] rounded-full bg-gray-100 px-[clamp(0.375rem,1.75cqw,0.5rem)] py-0.5 text-[clamp(0.625rem,calc(0.5rem+1.5cqw),0.75rem)] font-semibold text-gray-500">
          Coming Soon
        </span>
      )}
      <div
        className={`flex min-h-0 flex-1 flex-col font-medium text-[#847984] ${CARD_PADDING_CLASS} ${CARD_BODY_TEXT_CLASS}`}
      >
        <div
          className={`flex min-h-0 flex-1 flex-col justify-between ${CARD_PADDING_CLASS} ${CARD_SECTION_GAP_CLASS}`}
        >
          <div className="flex min-w-0 shrink-0 items-start gap-[clamp(0.25rem,1.5cqw,0.5rem)]">
            <img
              src="/ai-bulwark-logo.svg"
              alt=""
              aria-hidden
              className={`pointer-events-none mt-[0.15em] shrink-0 object-contain opacity-95 ${CARD_AI_LOGO_CLASS}`}
            />
            <span
              className={`header-gradient min-w-0 whitespace-pre-line font-bold leading-tight md:whitespace-nowrap xl:whitespace-pre-line ${CARD_TITLE_TEXT_CLASS}`}
            >
              {title}
            </span>
          </div>
          <div
            className={`flex min-w-0 shrink-0 flex-col ${CARD_LOGO_TO_DESC_GAP_CLASS}`}
          >
            {logoSrc ? (
              <img
                src={logoSrc}
                alt=""
                aria-hidden
                className={`pointer-events-none max-w-[70%] shrink-0 object-contain object-left ${CARD_MODULE_LOGO_CLASS}`}
              />
            ) : null}
            <p
              className={`line-clamp-3 min-w-0 whitespace-pre-line leading-snug ${CARD_BODY_TEXT_CLASS}`}
            >
              {description}
              <span
                aria-hidden
                className={`float-right clear-both ml-1 shrink-0 ${CARD_FAVICON_CLEARANCE_CLASS}`}
              />
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  if (!comingSoon && alertCount > 0) {
    return (
      <SimpleCircularIndicator
        count={alertCount}
        color={colors.danger[4]}
        size={28}
        fullWidth
        badgeClassName={CARD_ALERT_BADGE_CLASS}
      >
        {card}
      </SimpleCircularIndicator>
    );
  }

  return card;
};

const ModuleNavigation: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const fhirState = useFhirInitialization();
  const fhirClient = useFhirClient();
  useHomeModuleAlertCounts();
  const providerNPI = useAppSelector((state) => state?.provider?.providerNPI);
  const provider = useAppSelector((state) => state?.provider?.provider);
  const messageCount = useAppSelector((state) => state?.messages?.count);
  const {
    vbcCount,
    cdiCount,
    myQueriesCount,
    umQueriesCount,
    opcdQueriesCount,
    opcdHimAlertsCount,
    opcdCodingAlertsCount,
  } = useAppSelector((state) => state?.provider);

  const providerInfo = provider?.provider_info;
  const practitioner = fhirState?.practitioner;

  const providerLabel = getHomeScreenProviderLabel(
    providerInfo,
    fhirClient,
    practitioner
  );

  const { data: providerDetailsResponse } = useProviderDetails(providerNPI);

  useEffect(() => {
    if (providerDetailsResponse?.data?.data) {
      dispatch(setProvider(providerDetailsResponse.data.data));
    }
  }, [dispatch, providerDetailsResponse]);

  useEffect(() => {
    if (!fhirState.isInitialized) {
      return;
    }

    const token = localStorage.getItem(STORAGE_KEYS.JWT_TOKEN);
    const hasToken =
      token && token !== "" && token !== "null" && token !== "undefined";

    if (hasToken) {
      prefetchHomeReferenceData(providerNPI || undefined);
    }
  }, [fhirState.isInitialized, providerNPI]);

  useEffect(() => {
    if (fhirState?.hasFailed) {
      navigate(RouteConstants.ROUTE_UNAUTHORIZED);
    }
  }, [fhirState?.hasFailed, navigate]);

  const cdiAlertCount =
    Number(messageCount || 0) +
    Number(cdiCount || 0) +
    Number(myQueriesCount || 0);
  // My Queries + Messages + UM Alerts (UM Alerts temporarily uses CDI alerts pending count)
  const umAlertCount =
    Number(umQueriesCount || 0) +
    Number(messageCount || 0) +
    Number(cdiCount || 0);
  const vbcAlertCount = Number(vbcCount || 0);
  const opcdAlertCount =
    Number(opcdQueriesCount || 0) +
    Number(opcdHimAlertsCount || 0) +
    Number(opcdCodingAlertsCount || 0);

  const modules: Array<ModuleCardProps & { title: string; description: string }> = [
    {
      title: "Inpatient CDI",
      description: "Provider queries for Inpatient Clinical Documentation Integrity (IP-CDI)",
      logoSrc: "/arcplus-primary.svg",
      comingSoon: false,
      alertCount: cdiAlertCount,
      onClick: () => navigate(RouteConstants.ROUTE_CDI_MODULE),
      onPrefetch: prefetchCdiReferenceData,
    },
    {
      title: "Utilization Management",
      description:
        "AI for UM, Readmission Intelligence & Clinical Level-of-Care Evaluation",
      logoSrc: "/auricle-logo.webp",
      comingSoon: false,
      alertCount: umAlertCount,
      onClick: () => navigate(RouteConstants.ROUTE_UM_MODULE),
    },
    {
      title: "CAiRE",
      description:
        "Clinical AI-enabled Readmission Evaluator for PAC management",
      logoSrc: "/care-logo.png",
      comingSoon: false,
      alertCount: 0,
      onClick: () => {
        void invalidateCareManagementQuery();
        navigate(RouteConstants.ROUTE_CAIRE_MODULE);
      },
    },
    {
      title: "Value Based Care",
      description: "Risk Adjustment, Quality & Risk Stratification for VBC",
      logoSrc: "/RAQPlusPrimary.svg",
      comingSoon: false,
      alertCount: vbcAlertCount,
      onClick: () => navigate(RouteConstants.ROUTE_VBC_MODULE),
    },
    {
      title: "Outpatient CDI",
      description:
        "Provider Queries for Outpatient Clinical Documentation Integrity (OP-CDI)",
      logoSrc: "/arcplus-primary.svg",
      comingSoon: false,
      alertCount: opcdAlertCount,
      onClick: () => navigate(RouteConstants.ROUTE_OPCD_MODULE),
    },
    {
      title: "Clinical Care Guidelines",
      description:
        "Bulwark AI Clinical Care Guidelines for Medical Necessity Reviews",
      comingSoon: false,
      alertCount: 0,
      onClick: () => navigate(RouteConstants.ROUTE_CLINICAL_CARE_GUIDELINES),
      onPrefetch: prefetchClinicalCareGuidelines,
    },
    {
      title: "Encoder",
      description: DEFAULT_ENCODER_CATALOG.description,
      comingSoon: false,
      alertCount: 0,
      onClick: () => navigate(RouteConstants.ROUTE_ENCODER),
      onPrefetch: prefetchEncoderCatalog,
    },
  ];

  if (fhirState.isInitializing || !fhirState.isInitialized) {
    return (
      <div className="h-screen w-full">
        <SmartFhirLoadingScreen />
      </div>
    );
  }

  return (
    <div className="relative flex h-screen w-full min-h-0 flex-col overflow-hidden">
      <div className="w-full shrink-0 bg-white px-6 pb-4 pt-3 md:px-8">
        <div className="mx-auto flex w-full max-w-[1600px] items-center justify-between gap-4">
          <img
            src="/org-logo.svg"
            alt=""
            className="block h-auto min-w-0 max-h-14 w-auto object-contain object-left sm:max-h-16 md:max-h-20 lg:max-h-[6rem]"
          />
          <p className="shrink-0 text-sm font-medium text-gray-900">
            {providerLabel}
          </p>
        </div>
      </div>

      <div className="mt-4 min-h-0 flex-1 overflow-y-auto">
        <div className="w-full px-6 py-6 md:px-8">
          <div className="mx-auto grid w-full max-w-[1600px] items-stretch grid-cols-1 gap-4 min-[500px]:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
            {modules.map((module) => (
              <ModuleCard
                key={module.title}
                title={module.title}
                description={module.description}
                logoSrc={module.logoSrc}
                comingSoon={module.comingSoon}
                alertCount={module.alertCount}
                onClick={module.onClick}
                onPrefetch={module.onPrefetch}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModuleNavigation;
